<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useMediaQuery } from '@vueuse/core'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from '@/components/shadcn-ui/breadcrumb'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/shadcn-ui/dropdown-menu'
import type { BreadcrumbItem as BreadcrumbItemType } from '@/router/routes.config'

const route = useRoute()
const isDesktop = useMediaQuery('(min-width: 768px)')

interface BreadcrumbEntry {
  title: string
  to?: string
  isCurrent: boolean
}

const breadcrumbItems = computed<BreadcrumbEntry[]>(() => {
  const items: BreadcrumbEntry[] = []

  // Add parent breadcrumbs from route meta
  const parentBreadcrumbs = route.meta.breadcrumbs as BreadcrumbItemType[] | undefined
  if (parentBreadcrumbs) {
    for (const crumb of parentBreadcrumbs) {
      items.push({
        title: crumb.title,
        to: crumb.to,
        isCurrent: false,
      })
    }
  }

  // Add current page
  const currentTitle = route.meta.title as string | undefined
  if (currentTitle) {
    items.push({
      title: currentTitle,
      isCurrent: true,
    })
  }

  return items
})

// For mobile: show first, ellipsis with dropdown, and last item
const collapsedItems = computed(() => {
  if (breadcrumbItems.value.length <= 2) {
    return []
  }
  return breadcrumbItems.value.slice(1, -1)
})

const firstItem = computed(() => breadcrumbItems.value[0] ?? null)
const lastItem = computed(() => breadcrumbItems.value[breadcrumbItems.value.length - 1] ?? null)
const shouldCollapse = computed(
  () => !isDesktop.value && breadcrumbItems.value.length > 2 && firstItem.value && lastItem.value,
)
</script>

<template>
  <Breadcrumb>
    <BreadcrumbList>
      <!-- Desktop: Show all items -->
      <template v-if="isDesktop || breadcrumbItems.length <= 2">
        <template v-for="(item, index) in breadcrumbItems" :key="index">
          <BreadcrumbItem>
            <BreadcrumbLink v-if="!item.isCurrent && item.to" as-child>
              <RouterLink :to="{ name: item.to }">{{ item.title }}</RouterLink>
            </BreadcrumbLink>
            <BreadcrumbPage v-else>{{ item.title }}</BreadcrumbPage>
          </BreadcrumbItem>
          <BreadcrumbSeparator v-if="index < breadcrumbItems.length - 1" />
        </template>
      </template>

      <!-- Mobile: Collapse middle items -->
      <template v-else-if="shouldCollapse && firstItem && lastItem">
        <!-- First item -->
        <BreadcrumbItem>
          <BreadcrumbLink v-if="firstItem.to" as-child>
            <RouterLink :to="{ name: firstItem.to }">{{ firstItem.title }}</RouterLink>
          </BreadcrumbLink>
          <BreadcrumbPage v-else>{{ firstItem.title }}</BreadcrumbPage>
        </BreadcrumbItem>
        <BreadcrumbSeparator />

        <!-- Collapsed items dropdown -->
        <BreadcrumbItem v-if="collapsedItems.length > 0">
          <DropdownMenu>
            <DropdownMenuTrigger class="flex items-center gap-1" aria-label="Toggle menu">
              <BreadcrumbEllipsis />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem v-for="(item, index) in collapsedItems" :key="index" as-child>
                <RouterLink v-if="item.to" :to="{ name: item.to }">{{ item.title }}</RouterLink>
                <span v-else>{{ item.title }}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
        <BreadcrumbSeparator v-if="collapsedItems.length > 0" />

        <!-- Last item (current page) -->
        <BreadcrumbItem>
          <BreadcrumbPage>{{ lastItem.title }}</BreadcrumbPage>
        </BreadcrumbItem>
      </template>
    </BreadcrumbList>
  </Breadcrumb>
</template>
